import React, { useMemo } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useTranslate,
} from '@pankod/refine-core';
import {
  Avatar,
  Create,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  Input,
  Backdrop,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Button,
} from '@pankod/refine-mui';
import {
  Controller,
  useStepsForm,
} from '@pankod/refine-react-hook-form';
import { IPreviewVtuberFiles, IVtubers } from 'interfaces';
import _ from 'lodash';
import {
  ISelectedFile,
  useFileChangeHandler,
  useUploadFile,
} from 'hooks';
import { getVSTTime } from 'utils/utils';
import { Radio } from '@pankod/refine-mui';
import { CONST } from 'config/const';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import { PreviewVRM } from './preview-vrm';

const MAX_FILE_SIZE = 2097152; // 2MB

export const VtuberCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();

  const {
    refineCore: { onFinish, formLoading },
    register,
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useStepsForm<IVtubers, HttpError, IVtubers>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
  });

  const [format, setFormat] = React.useState<number>(
    CONST.FORMAT.PORTRAIT
  );
  const backgroundSize = useMemo(() => {
    const width = format === CONST.FORMAT.PORTRAIT ? 108 : 192;
    const height = format === CONST.FORMAT.PORTRAIT ? 192 : 108;

    return { width, height };
  }, [format]);

  const [searchParams, setSearchParams] = useSearchParams();
  const previewVRM = searchParams.get(CONST.PARAMS.PREVIEW_VRM);

  // Thumbnail
  const [selectedFile, setSelectedFile] =
    React.useState<ISelectedFile>({});
  const [preview, setPreview] = React.useState<IPreviewVtuberFiles>(
    {}
  );

  // Upload file
  const upload = useUploadFile();
  const onFileChangeHandler = useFileChangeHandler(
    setSelectedFile,
    { setError, clearErrors },
    { maxSize: MAX_FILE_SIZE }
  );
  const onVRMFileChangeHandler = useFileChangeHandler(
    setSelectedFile,
    { setError, clearErrors },
    { isSkipSizeCheck: true }
  );
  // Frame Image

  // create a preview as a side effect, whenever selected file is changed
  React.useEffect(() => {
    const objectUrls: IPreviewVtuberFiles = {};
    if (selectedFile?.thumb) {
      const objectUrl = URL.createObjectURL(selectedFile.thumb);
      objectUrls.thumbnail = objectUrl;
    }
    if (selectedFile?.background) {
      const objectUrl = URL.createObjectURL(selectedFile.background);
      objectUrls.background = objectUrl;
    }
    if (selectedFile?.vrm) {
      const objectUrl = URL.createObjectURL(selectedFile.vrm);
      objectUrls.vrm = objectUrl;
    }

    setPreview(objectUrls);

    // free memory when ever this component is unmounted
    return () => {
      Object.values(objectUrls).forEach((url) =>
        URL.revokeObjectURL(url)
      );
    };
  }, [selectedFile]);

  const handleSubmit = async () => {
    const updatedAt = getVSTTime(new Date()).toISOString();

    const [thumbnailUrl, backgroundUrl, vrmUrl] = await Promise.all([
      upload(selectedFile.thumb),
      upload(selectedFile.background),
      upload(selectedFile.vrm),
    ]);
    // image url
    setValue('thumb', thumbnailUrl ?? '');
    setValue('background', backgroundUrl ?? '');
    setValue('vrm', vrmUrl ?? '');

    const data = {
      ...getValues(),
      updated_at: updatedAt,
    };
    onFinish(data);
  };

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('name', {
                        required: t('errors.required.field', {
                          field: 'name',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Name (required)"
                    />
                    {errors.name && (
                      <FormHelperText error>
                        {errors.name.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Format
                    </Typography>
                    <Controller
                      control={control}
                      name="format"
                      defaultValue={CONST.FORMAT.PORTRAIT}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          onChange={(e, v) => {
                            setValue('format', +v);
                            setFormat(+v);
                          }}
                        >
                          <FormControlLabel
                            value={CONST.FORMAT.PORTRAIT}
                            control={<Radio />}
                            label="Portrait"
                          />
                          <FormControlLabel
                            value={CONST.FORMAT.LANDSCAPE}
                            control={<Radio />}
                            label="Landscape"
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Thumbnail
                    </Typography>
                    <Box
                      sx={{
                        height: 75,
                        width: 125,
                        border: '1px dashed grey',
                      }}
                    >
                      <label htmlFor="thumb-input">
                        <Input
                          id="thumb-input"
                          type="file"
                          sx={{
                            display: 'none',
                          }}
                          name="thumb"
                          onChange={onFileChangeHandler}
                        />
                        <input
                          id="file"
                          type="hidden"
                          {...register('thumb')}
                        />
                        <Avatar
                          variant="square"
                          sx={{
                            cursor: 'pointer',
                            width: {
                              xs: '125px',
                            },
                            height: {
                              xs: '75px',
                            },
                          }}
                          src={preview.thumbnail}
                          alt="Thumbnail"
                        />
                      </label>
                    </Box>
                    {errors.thumb && (
                      <FormHelperText error>
                        {errors.thumb.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Background */}
                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Background
                    </Typography>
                    <Box
                      sx={{
                        width: backgroundSize.width,
                        height: backgroundSize.height,
                        border: '1px dashed grey',
                      }}
                    >
                      <label htmlFor="background-input">
                        <Input
                          id="background-input"
                          type="file"
                          sx={{
                            display: 'none',
                          }}
                          name="background"
                          onChange={onFileChangeHandler}
                        />
                        <input
                          id="file"
                          type="hidden"
                          {...register('background')}
                        />
                        <Avatar
                          variant="square"
                          sx={{
                            cursor: 'pointer',
                            width: {
                              xs: `${backgroundSize.width}px`,
                            },
                            height: {
                              xs: `${backgroundSize.height}px`,
                            },
                          }}
                          src={preview.background}
                          alt="background"
                        />
                      </label>
                    </Box>
                    {errors.background && (
                      <FormHelperText error>
                        {errors.background.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* VRM */}
                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      VRM
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          height: 84,
                          width: 153,
                          border: '1px dashed grey',
                          display: 'flex',
                        }}
                      >
                        <label htmlFor="vrm-input">
                          <Input
                            id="vrm-input"
                            type="file"
                            sx={{
                              display: 'none',
                            }}
                            name="vrm"
                            onChange={onVRMFileChangeHandler}
                          />
                          <input
                            id="file"
                            type="hidden"
                            {...register('vrm')}
                          />
                          <Avatar
                            variant="square"
                            sx={{
                              cursor: 'pointer',
                              width: {
                                xs: `153px`,
                              },
                              height: {
                                xs: `84px`,
                              },
                            }}
                            alt="VRM"
                          />
                        </label>
                      </Box>

                      {/* Preview button if exist selectedFiles?.vrm */}
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!preview?.vrm}
                        sx={{
                          height: '40px',
                          marginLeft: '10px',
                          textTransform: 'none',
                        }}
                        onClick={() => {
                          if (!preview?.vrm) {
                            return;
                          }
                          searchParams.set(
                            CONST.PARAMS.PREVIEW_VRM,
                            'true'
                          );
                          setSearchParams(searchParams);
                        }}
                      >
                        {preview?.vrm
                          ? 'Preview'
                          : 'Preview (REQUIRED UPLOAD)'}
                      </Button>
                    </Box>
                    {errors.vrm && (
                      <FormHelperText error>
                        {errors.vrm.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  if (previewVRM && preview?.vrm) {
    return <PreviewVRM url={preview?.vrm} />;
  }

  return (
    <Create
      title={<Typography variant="h5">Create Vtuber</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Create>
  );
};
